import { getItemById } from '../../helpers/array.helpers';
import {
  ElementCategoryID,
  IElementCategory,
} from '../../models/element-categories.model';
import { systemCategories } from './categories-system.model';
import { productCategories } from './categories-product.model';
import { installationCategories } from './categories-installation.model';
import {
  OptionallyRequired,
  OptionallyRequiredOptions,
} from '../../models/type-helpers.model';

/**
 * All element categories available in the application.
 */
export const elementCategories: Readonly<IElementCategory[]> = Object.freeze([
  ...systemCategories,
  ...productCategories,
  ...installationCategories,
]);

/**
 * Get all ElementCategoryIDs that have generated children.
 */
const elementCategoriesWithGeneratedChildren = elementCategories.filter(
  (cat) => !!cat.getChildElements,
);

/**
 * Get all ElementCategoryIDs that have generated children.
 * Make this once to avoid triggering new renders of hooks.
 */
const elementCategoryIdsWithGeneratedChildren: ElementCategoryID[] =
  elementCategoriesWithGeneratedChildren.map((ec) => ec.id);

export const isCategoryIdsWithGeneratedChildren = (
  id?: ElementCategoryID | string,
): boolean =>
  !!id &&
  elementCategoryIdsWithGeneratedChildren.includes(id as ElementCategoryID);

/**
 * Get all available categories including those in use,
 * even if they are disabled.
 * @param usedIds
 * @returns
 */
export const getAvailableCategories = (
  ...usedIds: (ElementCategoryID | undefined)[]
): IElementCategory[] => {
  return elementCategories.filter(
    (cat) => !cat.disabled || usedIds.includes(cat.id),
  );
};

export const getElementCategoryById = <
  R extends OptionallyRequiredOptions = true,
>(
  id: ElementCategoryID,
  required: R = true as R,
): OptionallyRequired<IElementCategory, R> =>
  getItemById(
    elementCategories,
    id,
    required ? `Could not find element category "${id}"` : (false as R),
  );

/**
 * Get parent to a specific Element Category
 * @param id
 * @returns
 */
export const getParentCategory = (
  id: ElementCategoryID,
): Readonly<IElementCategory> | undefined => {
  const category = getElementCategoryById(id);
  if (category.parent_id) {
    return getElementCategoryById(category.parent_id);
  }
};
